/**
 * Translatable string field for App record forms: the plain base `TextField`
 * (bound to `values[field]` — the default-locale value lives in the base
 * column) plus, when the connection has 2+ configured languages, a globe
 * adornment opening a per-non-default-language translations popover that
 * writes `<field>__<code>` sibling keys via `onChange`.
 *
 * Mirrors the Designer `SynonymField` idiom (`designer/components/common/
 * PropertyPanel/SynonymField.tsx`): always renders the SAME element shape —
 * the base `TextField` unconditionally, the `Popover` gated on
 * `hasTranslations` — so the input never remounts (and doesn't drop an
 * in-flight caret/edit) the moment the async languages query resolves. Any
 * pre-existing end adornment (e.g. `FieldWidget`'s `node.inputSuffix`) is
 * composed with, not replaced.
 */
import React, { useState } from 'react'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Popover from '@mui/material/Popover'
import TextField from '@mui/material/TextField'
import LanguageIcon from '@mui/icons-material/Language'

import { useAppLocale } from '@/app/permissions/useAppLocale'

export interface TranslatableTextFieldProps {
  /** Base attribute name; siblings are read/written as `${field}__${code}`. */
  field: string
  /** Whole record — read-only source for the base value + every sibling. */
  values: Record<string, unknown>
  /** Fired with the key being edited (`field` or `field__code`) and the new value. */
  onChange: (key: string, value: string) => void
  disabled?: boolean
  required?: boolean
  readOnly?: boolean
  /** Caption. App forms leave it unset — `FieldWidget` renders its own. */
  label?: string
  placeholder?: string
  maxLength?: number
  type?: string
  startAdornment?: React.ReactNode
  /** Pre-existing suffix adornment (e.g. `node.inputSuffix`) — composed with the globe icon. */
  endAdornment?: React.ReactNode
}

export function TranslatableTextField({
  field,
  values,
  onChange,
  disabled,
  required,
  readOnly,
  label,
  placeholder,
  maxLength,
  type,
  startAdornment,
  endAdornment,
}: TranslatableTextFieldProps) {
  const { languages } = useAppLocale()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const hasTranslations = languages.length > 1

  const defaultCode = languages.find((l) => l.isDefault)?.code
  const otherLanguages = languages.filter((l) => l.code !== defaultCode)
  const filledCount = otherLanguages.filter((lang) => {
    const v = values[`${field}__${lang.code}`]
    return typeof v === 'string' && v.trim() !== ''
  }).length

  const value = values[field]

  return (
    <>
      <TextField
        value={value == null ? '' : String(value)}
        onChange={(e) => {
          let v = e.target.value
          if (maxLength != null && v.length > maxLength) v = v.slice(0, maxLength)
          onChange(field, v)
        }}
        disabled={disabled}
        required={required}
        size="small"
        fullWidth
        type={type}
        label={label}
        placeholder={placeholder}
        inputProps={{ maxLength: maxLength ?? undefined, 'aria-label': label }}
        InputProps={{
          readOnly,
          startAdornment,
          endAdornment: (
            <>
              {endAdornment}
              {hasTranslations && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={`${field} translations`}
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                  >
                    <Badge badgeContent={filledCount} color="primary">
                      <LanguageIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </InputAdornment>
              )}
            </>
          ),
        }}
      />
      {hasTranslations && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 240 }}>
            {otherLanguages.map((lang) => {
              const siblingKey = `${field}__${lang.code}`
              const siblingValue = values[siblingKey]
              return (
                <TextField
                  key={lang.code}
                  size="small"
                  fullWidth
                  label={lang.name}
                  value={siblingValue == null ? '' : String(siblingValue)}
                  onChange={(e) => onChange(siblingKey, e.target.value)}
                />
              )
            })}
          </Box>
        </Popover>
      )}
    </>
  )
}
